import axios, { AxiosInstance, AxiosError, isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/api';
import { PlatformService } from '../utils/platform';
import { getTokenRemainingMs } from './auth';
import { isQuietAccessRefusal, isQuietDeliveryFailure, isQuietNotFound } from '../utils/quietNotFound';

/** Refresh the access token 60 seconds before it expires */
const REFRESH_BUFFER_MS = 60_000;

/** Whether a failed /auth/refresh was the server refusing the refresh token, not a transport error. */
export function isRefreshRejected(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

class ApiService {
  private api: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isRedirecting = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => {
        // The server used the user's current role/tenant because this token's claims were out
        // of date. Refresh in the background so the next requests carry a current token.
        if (response.headers?.['x-token-stale']) {
          this.refreshAccessToken().catch(() => {
            // A failed background refresh is retried by the timer or the next 401.
          });
        }
        return response;
      },
      async (error: AxiosError) => {
        const original = error.config as any;
        
        if (error.response?.status === 401 && original && !original._retry) {
          original._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            if (newToken) {
              original.headers = original.headers || {};
              original.headers.Authorization = `Bearer ${newToken}`;
              return this.api(original);
            } else {
              this.redirectToLogin();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            // Only a refresh the server refused ends the session; a network or server error
            // leaves the user signed in so the request can be retried.
            if (isRefreshRejected(refreshError)) {
              this.redirectToLogin();
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle 402 Payment Required - Subscription expired/suspended
        if (error.response?.status === 402) {
          const data = error.response.data as any;
          const code = data?.code || 'SUBSCRIPTION_INACTIVE';
          const message = data?.message || 'Your subscription has expired.';

          // Show appropriate message based on error code
          if (code === 'NO_ACTIVE_SUBSCRIPTION' || code === 'SUBSCRIPTION_EXPIRED') {
            toast.error('Your subscription has expired. Data is available in read-only mode.', {
              duration: 6000,
              icon: '\u26A0\uFE0F'
            });
          } else if (code === 'SUBSCRIPTION_SUSPENDED') {
            toast.error('Your subscription is suspended. Please contact support.', {
              duration: 6000,
              icon: '\u26A0\uFE0F'
            });
          } else if (code === 'ENCOUNTER_LIMIT_REACHED' || code === 'OVERAGE_LIMIT_REACHED') {
            toast.error('Usage limit reached. Please upgrade your plan.', {
              duration: 6000,
              icon: '\u26A0\uFE0F'
            });
          } else {
            toast.error(message, { duration: 6000 });
          }

          // Dispatch custom event for components to react to subscription issues
          window.dispatchEvent(new CustomEvent('subscription:blocked', {
            detail: {
              code,
              message,
              readOnlyMode: data?.readOnlyMode || true,
              usage: data?.usage,
              upgradeUrl: data?.upgradeUrl || '/settings/billing'
            }
          }));

          return Promise.reject(error);
        }

        // Handle 451 — Legal acceptance required
        // Reject so callers can handle it; ProtectedRoute gates rendering
        if (error.response?.status === 451) {
          return Promise.reject(error);
        }

        // A record the user can't open has its own Access Denied screen on the page
        const requestUrl = (original?.url || (error.config as any)?.url || '') as string;
        const errorCode = (error.response?.data as { code?: string } | undefined)?.code;
        if (isQuietAccessRefusal(error.response?.status, requestUrl, original?.method, errorCode)) {
          return Promise.reject(error);
        }

        // Handle other errors
        if (error.response && error.response.status >= 500) {
          const url = (original?.url || (error.config as any)?.url || '') as string;
          if (!isQuietDeliveryFailure(error.response.status, url)) {
            toast.error('Server error. Please try again later.');
          }
        } else if (error.response?.status === 403) {
          // A recording refused for the user's role has its own message where it happens (AMP-136).
          if ((error.response.data as { code?: string } | undefined)?.code !== 'RECORDING_ROLE_REQUIRED') {
            toast.error('Access denied. You don\'t have permission for this action.');
          }
        } else if (error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data) {
          // Expected 404s (no medical note yet, no procedure CPT audit yet) are handled by the caller.
          const url = (original?.url || (error.config as any)?.url || '') as string;
          if (!isQuietNotFound(error.response?.status, url, original?.method)) {
            toast.error((error.response.data as any).error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  /**
   * Schedule a silent token refresh before the access token expires.
   * Called after every successful login or token refresh.
   */
  private scheduleTokenRefresh(accessToken: string) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const remainingMs = getTokenRemainingMs(accessToken);
    if (remainingMs <= 0) return;

    // Refresh 60s before expiry, but at least 5s from now
    const delay = Math.max(remainingMs - REFRESH_BUFFER_MS, 5_000);

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshAccessToken();
      } catch {
        // Proactive refresh failed — the reactive 401 interceptor will handle it
      }
    }, delay);
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
          refreshToken,
        });

        // setTokens (a password change) replaced the pair while this was in flight. That pair is
        // newer than this response, which may carry the version the change just revoked.
        if (this.getRefreshToken() !== refreshToken) {
          return this.getAccessToken();
        }
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        this.scheduleTokenRefresh(accessToken);
        return accessToken;
      } catch (error: any) {
        // Same race: the refusal is for the replaced pair, not for the one now stored.
        if (this.getRefreshToken() !== refreshToken && this.getRefreshToken()) {
          return this.getAccessToken();
        }
        // Only a refresh the server refused (400/401/403) ends the session. A network error or a
        // 5xx keeps both tokens, so a blip while a clinician is recording does not sign them out;
        // the timer or the next 401 tries again.
        if (isRefreshRejected(error)) {
          const errorCode = error?.response?.data?.code;
          if (errorCode === 'ACCOUNT_DEACTIVATED') {
            sessionStorage.setItem('deactivation_reason', 'org_deactivated');
          }
          this.logout();
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * The current access token, refreshed first if it has less than `minRemainingMs` left. For
   * callers outside this axios instance (recording upload and polling) that would otherwise send
   * a token the timer did not get to renew, e.g. after the device slept. A refresh that fails on
   * the network returns the existing token so the request is still attempted.
   */
  async ensureFreshAccessToken(minRemainingMs: number): Promise<string | null> {
    const token = this.getAccessToken();
    if (!token) return null;
    if (getTokenRemainingMs(token) > minRemainingMs) return token;
    try {
      return (await this.refreshAccessToken()) ?? this.getAccessToken();
    } catch (error) {
      if (isRefreshRejected(error)) {
        // The session is over; send the user to login like the main interceptor does, instead of
        // leaving them on a protected page with no tokens.
        this.redirectToLogin();
        return null;
      }
      return this.getAccessToken();
    }
  }

  /**
   * Refresh now, after a request was refused with 401. Null when the session cannot be renewed;
   * when the server refused the refresh (or there is no refresh token) this also redirects to
   * login. A network failure returns null without redirecting.
   */
  async refreshAfterUnauthorized(): Promise<string | null> {
    try {
      const token = await this.refreshAccessToken();
      if (!token) this.redirectToLogin();
      return token;
    } catch (error) {
      if (isRefreshRejected(error)) this.redirectToLogin();
      return null;
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    this.scheduleTokenRefresh(accessToken);
  }

  logout() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Redirect to login once. Prevents multiple concurrent 401s from
   * each triggering their own redirect.
   */
  private redirectToLogin() {
    this.logout();
    if (this.isRedirecting) return;
    this.isRedirecting = true;
    const loginPath = PlatformService.isNative() ? '/mobile-login' : '/login';
    window.location.href = loginPath;
  }

  // Generic API methods
  async get<T>(url: string, params?: any, config?: { signal?: AbortSignal }): Promise<T> {
    const response = await this.api.get(url, { params, ...config });
    return response.data;
  }

  /**
   * GET a file through the authenticated client. Returns the body and the file name from
   * Content-Disposition, when the server sends one.
   */
  async getBlob(url: string): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.api.get(url, { responseType: 'blob' });
    const disposition: string | undefined = response.headers?.['content-disposition'];
    const match = disposition?.match(/filename="?([^";]+)"?/i);
    return { blob: response.data, filename: match ? match[1] : null };
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.api.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.api.delete(url);
    return response.data;
  }

  // File upload with progress
  async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('audio', file);

    const response = await this.api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Clinical API methods
  async triggerAIProcessing(appointmentId: string): Promise<any> {
    try {
      const response = await this.post(`/v1/clinical/${appointmentId}/trigger-processing`);
      toast.success('AI processing triggered successfully');
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to trigger AI processing';
      toast.error(errorMessage);
      throw error;
    }
  }

  async enrichTranscription(appointmentId: string): Promise<any> {
    try {
      const response = await this.post(`/v1/appointments/${appointmentId}/enrich-transcription`);
      toast.success('Transcription enrichment started');
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to start transcription enrichment';
      toast.error(errorMessage);
      throw error;
    }
  }

  // New Clinical Pipeline API methods - connects to your working backend
  async getClinicalData(appointmentId: string): Promise<any> {
    return await this.get(`/v1/clinical/${appointmentId}/data`);
  }

  async getClinicalSuggestions(appointmentId: string): Promise<any> {
    return await this.get(`/v1/clinical/${appointmentId}/suggestions`);
  }

  async getQualityGates(appointmentId: string): Promise<any> {
    return await this.get(`/v1/clinical/${appointmentId}/quality-gates`);
  }

  async approveSuggestion(appointmentId: string, suggestionId: string, comment?: string): Promise<any> {
    return await this.post(`/v1/clinical/${appointmentId}/suggestions/${suggestionId}/approve`, { comment });
  }

  async rejectSuggestion(appointmentId: string, suggestionId: string, reason: string): Promise<any> {
    return await this.post(`/v1/clinical/${appointmentId}/suggestions/${suggestionId}/reject`, { reason });
  }

  async updateSuggestion(suggestionId: string, data: { status: string; text?: string; reason?: string }): Promise<any> {
    return await this.put(`/v1/clinical/suggestions/${suggestionId}`, data);
  }

  async bulkUpdateSuggestions(data: { suggestionIds: string[]; status: string; reason?: string }): Promise<any> {
    return await this.put(`/v1/clinical/suggestions/bulk`, data);
  }

  async getSuggestionEvidence(appointmentId: string, suggestionId: string): Promise<any> {
    return await this.get(`/v1/clinical/${appointmentId}/suggestions/${suggestionId}/evidence`);
  }

  // Medical Note Generation API methods
  async getNoteTemplates(scope?: string): Promise<any> {
    const params = scope ? { scope } : {};
    return await this.get('/note-templates', params);
  }

  // Subscription API methods
  async checkCanRecord(): Promise<{
    success: boolean;
    data: {
      canRecord: boolean;
      reason?: string;
      message?: string;
      usage?: {
        encountersUsed: number;
        encounterLimit: number;
        percentUsed: number;
      };
    };
  }> {
    return await this.get('/v1/organization/subscription/can-record');
  }
}

export const apiService = new ApiService();

/**
 * Check if an error is a 402 subscription error.
 * The Axios interceptor already shows a toast for these,
 * so components should NOT show their own toast or console.error.
 */
export function isSubscriptionError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 402;
}

